import { t } from '../i18n';

const firebaseErrorMap: Record<string, Parameters<typeof t>[0]> = {
  'auth/invalid-credential': 'errors.firebase.invalidCredential',
  'auth/wrong-password': 'errors.firebase.invalidCredential',
  'auth/user-not-found': 'errors.firebase.userNotFound',
  'auth/email-already-in-use': 'errors.firebase.emailAlreadyInUse',
  'auth/weak-password': 'errors.firebase.weakPassword',
  'auth/invalid-email': 'errors.firebase.invalidEmail',
  'auth/user-disabled': 'errors.firebase.userDisabled',
  'auth/too-many-requests': 'errors.firebase.tooManyRequests',
  'auth/network-request-failed': 'errors.firebase.networkRequestFailed',
  'auth/requires-recent-login': 'errors.firebase.requiresRecentLogin',
  'auth/popup-closed-by-user': 'errors.firebase.popupClosedByUser',
  'auth/popup-blocked': 'errors.firebase.popupBlocked',
  'auth/cancelled-popup-request': 'errors.firebase.cancelledPopupRequest',
  'auth/operation-not-allowed': 'errors.firebase.operationNotAllowed',
  'auth/account-exists-with-different-credential': 'errors.firebase.accountExistsWithDifferentCredential',
  'auth/credential-already-in-use': 'errors.firebase.credentialAlreadyInUse',
  'auth/invalid-verification-code': 'errors.firebase.invalidVerificationCode',
  'auth/invalid-verification-id': 'errors.firebase.invalidVerificationId',
  'auth/missing-verification-code': 'errors.firebase.missingVerificationCode',
  'auth/missing-verification-id': 'errors.firebase.missingVerificationId',
  'permission-denied': 'errors.firebase.permissionDenied',
  'unavailable': 'errors.firebase.unavailable',
  'deadline-exceeded': 'errors.firebase.deadlineExceeded',
  'not-found': 'errors.firebase.notFound',
  'already-exists': 'errors.firebase.alreadyExists',
  'resource-exhausted': 'errors.firebase.resourceExhausted',
  'failed-precondition': 'errors.firebase.failedPrecondition',
  'aborted': 'errors.firebase.aborted',
  'out-of-range': 'errors.firebase.outOfRange',
  'unimplemented': 'errors.firebase.unimplemented',
  'internal': 'errors.firebase.internal',
  'data-loss': 'errors.firebase.dataLoss',
  'unauthenticated': 'errors.firebase.unauthenticated',
};

/**
 * Convert Firebase error codes to user-friendly messages
 */
export const getFirebaseErrorMessage = (error: any): string => {
  if (!error) return t('errors.firebase.unexpected');

  const errorCode = error.code || '';
  const errorMessage = error.message || '';

  const mapped = firebaseErrorMap[errorCode];
  if (mapped) return t(mapped);

  // Check if error message contains common patterns
  if (errorMessage.toLowerCase().includes('network')) {
    return t('errors.firebase.networkError');
  }
  if (errorMessage.toLowerCase().includes('timeout')) {
    return t('errors.firebase.timeoutError');
  }
  if (errorMessage.toLowerCase().includes('permission')) {
    return t('errors.firebase.permissionDenied');
  }

  console.error('Unhandled Firebase error:', { code: errorCode, message: errorMessage });
  return t('errors.firebase.genericError');
};

/**
 * Get user-friendly error message for payment/billing errors
 */
export const getPaymentErrorMessage = (error: any): string => {
  if (!error) return t('errors.payment.default');

  const errorMessage = (error.message || '').toLowerCase();

  if (errorMessage.includes('card') && (errorMessage.includes('declined') || errorMessage.includes('insufficient'))) {
    return t('errors.payment.cardDeclined');
  }
  if (errorMessage.includes('expired')) {
    return t('errors.payment.cardExpired');
  }
  if (errorMessage.includes('incorrect') || errorMessage.includes('invalid')) {
    return t('errors.payment.cardIncorrect');
  }
  if (errorMessage.includes('network') || errorMessage.includes('connection')) {
    return t('errors.payment.network');
  }
  if (errorMessage.includes('not found')) {
    return t('errors.payment.cardNotFound');
  }
  if (errorMessage.includes('authentication') || errorMessage.includes('3d secure')) {
    return t('errors.payment.authenticationFailed');
  }
  if (errorMessage.includes('customer')) {
    return t('errors.payment.customerFailed');
  }
  if (errorMessage.includes('subscription')) {
    return t('errors.payment.subscriptionFailed');
  }

  // Return the original error message if it seems user-friendly
  if (error.message && error.message.length < 100 && !error.message.includes('Error:')) {
    return error.message;
  }

  console.error('Payment error:', error);
  return t('errors.payment.genericWithSupport');
};
